/**
 * Операции с материалами, которых нет в дизайне прототипа: меню файла (⋯ и правый клик), переименование,
 * копия, перенос, доступ и личные папки, удаление с отменой, корзина, ⌘K. Оформлены токенами и приёмами прототипа.
 */
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FEAT } from './features';
import { http } from './live-api';
import { blocksToText } from './md-blocks';
import { overlay, useOverlay } from './overlay-store';

type Ctl = any;
type Res = any;

const ic = (name: string): CSSProperties => ({
  width: 16,
  height: 16,
  flex: 'none',
  background: 'currentColor',
  WebkitMask: `url(/icons/lucide/${name}.svg) center/contain no-repeat`,
  mask: `url(/icons/lucide/${name}.svg) center/contain no-repeat`,
});
const Icon = ({ name, size = 16, style }: { name: string; size?: number; style?: CSSProperties }) => (
  <span aria-hidden style={{ ...ic(name), width: size, height: size, ...style }} />
);

const kindIcon = (r: Res) =>
  r.kind === 'folder'
    ? r.personal
      ? 'folder-lock'
      : 'folder'
    : r.kind === 'note'
      ? 'file-text'
      : r.kind === 'mindmap' && r.content?.style?.kind === 'board'
        ? 'sticky-note'
        : r.kind === 'mindmap'
          ? 'network'
          : r.kind === 'task'
            ? 'square-check-big'
            : 'file';
const kindLabel = (r: Res) =>
  r.kind === 'folder'
    ? 'Папка'
    : r.kind === 'note'
      ? 'Документ'
      : r.kind === 'task'
        ? 'Задача'
        : r.content?.style?.kind === 'board'
          ? 'Доска'
          : 'Майнд-карта';

const can = (r: Res | undefined, need: 'edit' | 'manage') =>
  !!r && (need === 'edit' ? r.permission === 'edit' || r.permission === 'manage' : r.permission === 'manage');

const panel: CSSProperties = {
  background: 'hsl(var(--popover))',
  color: 'hsl(var(--foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 14,
  boxShadow: '0 18px 44px rgba(16,24,40,.14)',
  fontSize: 13,
};
const btn = (primary = false, danger = false): CSSProperties => ({
  height: 34,
  padding: '0 14px',
  borderRadius: 10,
  border: primary || danger ? 0 : '1px solid hsl(var(--border))',
  background: danger ? 'hsl(var(--destructive))' : primary ? 'hsl(var(--primary))' : 'hsl(var(--background))',
  color: primary || danger ? '#fff' : 'hsl(var(--foreground))',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
});
const input: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  height: 36,
  border: '1px solid hsl(var(--input))',
  borderRadius: 10,
  padding: '0 12px',
  fontSize: 13.5,
  background: 'hsl(var(--background))',
  color: 'hsl(var(--foreground))',
  outline: 0,
};

const errText = (e: any) =>
  e?.code === 'version_conflict'
    ? 'Материал только что изменили — обновите и повторите'
    : e?.code === 'subtree_forbidden'
      ? 'Внутри есть материалы, к которым у вас нет доступа'
      : e?.code === 'forbidden' || e?.status === 403
        ? 'Недостаточно прав'
        : e?.message || 'Не получилось';

function Modal({
  title,
  sub,
  children,
  onClose,
  width = 440,
}: {
  title: string;
  sub?: string;
  children: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  return (
    <div
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 400,
        background: 'rgba(16,20,28,.32)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '12vh 16px 16px',
      }}
    >
      <div
        role="dialog"
        aria-modal
        aria-label={title}
        style={{
          ...panel,
          borderRadius: 18,
          width: '100%',
          maxWidth: width,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.01em' }}>{title}</div>
            {sub && <div style={{ marginTop: 4, color: 'hsl(var(--muted-foreground))', lineHeight: 1.45 }}>{sub}</div>}
          </div>
          <button
            aria-label="Закрыть"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: 0,
              borderRadius: 9,
              background: 'transparent',
              cursor: 'pointer',
              color: 'hsl(var(--muted-foreground))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- меню файла ----------

function FileMenu({ ctl, id, x, y }: { ctl: Ctl; id: string; x: number; y: number }) {
  const r = ctl.res(id);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth,
      h = el.offsetHeight;
    setPos({ left: Math.min(x, innerWidth - w - 8), top: Math.min(y, innerHeight - h - 8) });
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) overlay.set({ menu: null });
    };
    // Клавиатура: фокус на первый пункт, стрелки — по пунктам, Escape — закрыть и вернуть фокус туда, откуда открыли.
    const prev = document.activeElement as HTMLElement | null;
    const items = () => [...el.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    items()[0]?.focus({ preventScroll: true });
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') overlay.set({ menu: null });
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const list = items(),
          i = list.indexOf(document.activeElement as HTMLElement);
        list[(i + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length]?.focus();
      }
    };
    // Подписываемся на следующем тике (иначе закроет тот же клик, что открыл меню) — и отменяем, если меню уже закрыли.
    const t = setTimeout(() => document.addEventListener('mousedown', off), 0);
    document.addEventListener('keydown', esc);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', esc);
      // фокус остался на закрытом меню (или потерялся) — вернуть; если открылся диалог, он уже забрал фокус
      const a = document.activeElement;
      if (prev?.isConnected && (!a || a === document.body || el.contains(a))) prev.focus({ preventScroll: true });
    };
  }, [x, y]);
  if (!r) return null;
  const SEP = 'sep' as const;
  type Item = [string, string, () => void, boolean?];
  const all: (Item | typeof SEP | null)[] = [
    [
      'Открыть',
      'arrow-up-right',
      () => {
        overlay.close();
        ctl.open(id);
      },
    ],
    can(r, 'edit') ? ['Переименовать', 'pencil', () => overlay.openDialog('rename', id)] : null,
    can(r, 'edit')
      ? [
          'Дублировать',
          'copy',
          () => {
            overlay.close();
            ctl.duplicateRes(id);
          },
        ]
      : null,
    can(r, 'manage') && (r.kind !== 'folder' || r.parent_id)
      ? ['Переместить…', 'folder-input', () => overlay.openDialog('move', id)]
      : null,
    can(r, 'manage') ? ['Доступ…', 'shield', () => overlay.openDialog('access', id)] : null,
    can(r, 'manage') ? ['Удалить', 'trash-2', () => overlay.openDialog('delete', id), true] : null,
    SEP,
    ['Корзина проекта', 'archive-restore', () => overlay.openDialog('trash')],
  ];
  const items = all.filter((x): x is Item | typeof SEP => x !== null);
  return (
    <div
      ref={ref}
      role="menu"
      style={{ ...panel, position: 'fixed', zIndex: 410, left: pos.left, top: pos.top, minWidth: 210, padding: 6 }}
    >
      <div
        style={{
          padding: '6px 10px 8px',
          color: 'hsl(var(--muted-foreground))',
          fontSize: 11.5,
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          maxWidth: 260,
        }}
      >
        <Icon name={kindIcon(r)} size={13} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
      </div>
      {items.map((it, i) =>
        it !== SEP ? (
          <button
            key={it[0]}
            role="menuitem"
            onClick={it[2]}
            data-ai="1"
            className="dc0"
            style={{
              width: '100%',
              height: 32,
              border: 0,
              borderRadius: 9,
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 10px',
              cursor: 'pointer',
              fontSize: 13,
              color: it[3] ? 'hsl(var(--destructive))' : 'hsl(var(--foreground))',
              textAlign: 'left',
            }}
          >
            <span data-ic="1" style={{ ...ic(it[1]), opacity: 0.75 }} />
            {it[0]}
          </button>
        ) : (
          <div key={'sep' + i} style={{ height: 1, background: 'hsl(var(--border))', margin: '5px 4px' }} />
        ),
      )}
    </div>
  );
}

// ---------- диалоги ----------

function RenameDialog({ ctl, id }: { ctl: Ctl; id: string }) {
  const r = ctl.res(id);
  const [title, setTitle] = useState(r?.title ?? '');
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const save = async () => {
    const t = title.trim();
    if (!t || t === r.title) return overlay.close();
    setBusy(true);
    try {
      await ctl.api.renameResource(id, r.version, t.slice(0, 160));
      overlay.close();
      await ctl.refresh();
    } catch (e) {
      setBusy(false);
      overlay.snack(errText(e));
    }
  };
  return (
    <Modal title="Переименовать" sub={kindLabel(r)} onClose={overlay.close}>
      <input
        autoFocus
        value={title}
        maxLength={160}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        style={input}
        onFocus={(e) => e.target.select()}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btn()} onClick={overlay.close}>
          Отмена
        </button>
        <button style={{ ...btn(true), opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={save}>
          Сохранить
        </button>
      </div>
    </Modal>
  );
}

function MoveDialog({ ctl, id }: { ctl: Ctl; id: string }) {
  const r = ctl.res(id);
  const rs: Res[] = ctl.state.snap?.resources ?? [];
  // Дерево дизайна показывает один уровень вложенности: в папку можно класть файлы, папки — только в корень.
  const targets =
    r?.kind === 'folder'
      ? []
      : rs.filter((x) => x.kind === 'folder' && !x.parent_id && x.id !== r?.parent_id && can(x, 'edit'));
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const move = async (parentId: string | null) => {
    setBusy(true);
    try {
      await ctl.api.moveResource(id, { parent_id: parentId, version: r.version });
      overlay.close();
      await ctl.refresh();
      overlay.snack('Перемещено');
    } catch (e) {
      setBusy(false);
      overlay.snack(errText(e));
    }
  };
  const row = (key: string, icon: string, label: string, onClick: () => void) => (
    <button
      key={key}
      disabled={busy}
      onClick={onClick}
      data-ai="1"
      className="dc0"
      style={{
        height: 38,
        border: 0,
        borderRadius: 10,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 10px',
        cursor: 'pointer',
        fontSize: 13,
        color: 'hsl(var(--foreground))',
        textAlign: 'left',
      }}
    >
      <span data-ic="1" style={{ ...ic(icon), opacity: 0.7 }} />
      {label}
    </button>
  );
  return (
    <Modal
      title={`Переместить «${r.title}»`}
      sub={
        r.kind === 'folder'
          ? 'Папки живут на верхнем уровне проекта.'
          : 'Выберите папку. Доступ к файлу будет наследоваться от неё.'
      }
      onClose={overlay.close}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 320, overflow: 'auto' }}>
        {r.parent_id && row('root', 'layout-grid', 'Без папки (верхний уровень)', () => move(null))}
        {targets.map((f) =>
          row(f.id, f.personal ? 'folder-lock' : 'folder', f.title + (f.personal ? ' · личная' : ''), () => move(f.id)),
        )}
        {!r.parent_id && !targets.length && (
          <div style={{ color: 'hsl(var(--muted-foreground))', padding: '6px 2px' }}>
            Некуда перемещать: создайте папку.
          </div>
        )}
      </div>
    </Modal>
  );
}

function AccessDialog({ ctl, id }: { ctl: Ctl; id: string }) {
  const r = ctl.res(id);
  const sid = ctl.state.sid;
  const [grants, setGrants] = useState<any[] | null>(null);
  const [adding, setAdding] = useState('');
  const [audience, setAudience] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const members: any[] = ctl.state.memData?.[sid]?.list ?? [];
  const meId = ctl._me?.id;
  useEffect(() => {
    ctl.loadMembers(sid);
    ctl.api.getAccess(id).then(setGrants, (e: any) => overlay.snack(errText(e)));
  }, [ctl, id, sid]);
  if (!r) return null;
  const save = async (next: any[]) => {
    setBusy(true);
    try {
      const fresh = await ctl.api.getResource(id);
      const out = await ctl.api.putAccess(
        id,
        fresh.version,
        next.map((g) => ({ userId: g.userId, permission: g.permission })),
      );
      setGrants(out);
      await ctl.refresh();
    } catch (e) {
      overlay.snack(errText(e));
    }
    setBusy(false);
  };
  const askPersonal = async () => {
    try {
      setAudience(await ctl.api.audience(id, !r.personal));
    } catch (e) {
      overlay.snack(errText(e));
    }
  };
  const applyPersonal = async () => {
    setBusy(true);
    try {
      const fresh = await ctl.api.getResource(id);
      await ctl.api.setPersonal(id, fresh.version, !r.personal);
      setAudience(null);
      await ctl.refresh();
      overlay.snack(!r.personal ? 'Папка стала личной' : 'Папка снова общая');
    } catch (e) {
      overlay.snack(errText(e));
    }
    setBusy(false);
  };
  const free = members.filter((m) => m.userId !== meId && !(grants ?? []).some((g) => g.userId === m.userId));
  const permLabel: Record<string, string> = {
    none: 'нет доступа',
    view: 'просмотр',
    edit: 'редактирование',
    manage: 'управление',
  };
  return (
    <Modal
      title={`Доступ: «${r.title}»`}
      sub="Роли спейса дают доступ всем участникам проекта. Здесь — дополнительный доступ конкретным людям: к этому материалу и всему внутри."
      onClose={overlay.close}
      width={500}
    >
      {r.kind === 'folder' && (
        <div
          style={{
            border: '1px solid hsl(var(--border))',
            borderRadius: 12,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="folder-lock" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>Личная папка</div>
              <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 12 }}>
                Видят только автор, админы и те, кому выдан доступ ниже.
              </div>
            </div>
            <button
              aria-pressed={!!r.personal}
              onClick={askPersonal}
              disabled={busy}
              style={{
                width: 36,
                height: 20,
                borderRadius: 99,
                border: 0,
                cursor: 'pointer',
                background: r.personal ? 'hsl(var(--primary))' : 'hsl(var(--input))',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: r.personal ? 18 : 2,
                  width: 16,
                  height: 16,
                  borderRadius: 99,
                  background: '#fff',
                  transition: 'left .2s',
                }}
              />
            </button>
          </div>
          {audience && (
            <div
              style={{
                background: 'hsl(var(--muted))',
                borderRadius: 10,
                padding: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                {audience.length ? 'Изменится доступ:' : 'Ни у кого из участников доступ не изменится.'}
              </div>
              {audience.map((a) => (
                <div key={a.userId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>{a.name}</span>
                  <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {permLabel[a.before]} → {permLabel[a.after]}
                  </span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button style={btn()} onClick={() => setAudience(null)}>
                  Отмена
                </button>
                <button style={btn(true)} disabled={busy} onClick={applyPersonal}>
                  {r.personal ? 'Сделать общей' : 'Сделать личной'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {grants === null ? (
          <div style={{ color: 'hsl(var(--muted-foreground))' }}>Загружаем…</div>
        ) : grants.length ? (
          grants.map((g) => (
            <div key={g.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, height: 38 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 99,
                  background: 'hsl(var(--primary) / .12)',
                  color: 'hsl(var(--primary))',
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {(g.name || '?')
                  .split(' ')
                  .map((w: string) => w[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{g.name}</div>
                <div style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))' }}>{g.email}</div>
              </div>
              <select
                value={g.permission}
                disabled={busy}
                onChange={(e) =>
                  save(grants.map((x) => (x.userId === g.userId ? { ...x, permission: e.target.value } : x)))
                }
                style={{ ...input, width: 'auto', height: 30, fontSize: 12 }}
              >
                <option value="view">Просмотр</option>
                <option value="edit">Редактирование</option>
              </select>
              <button
                aria-label="Убрать доступ"
                disabled={busy}
                onClick={() => save(grants.filter((x) => x.userId !== g.userId))}
                style={{ ...btn(), padding: 0, width: 30, height: 30, justifyContent: 'center' }}
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          ))
        ) : (
          <div style={{ color: 'hsl(var(--muted-foreground))' }}>Дополнительного доступа нет.</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={adding} onChange={(e) => setAdding(e.target.value)} style={{ ...input, flex: 1 }}>
          <option value="">Выдать доступ участнику…</option>
          {free.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name} · {m.email}
            </option>
          ))}
        </select>
        <button
          style={{ ...btn(true), opacity: adding ? 1 : 0.5 }}
          disabled={!adding || busy}
          onClick={() => {
            const m = members.find((x) => x.userId === adding);
            setAdding('');
            save([...(grants ?? []), { userId: adding, name: m?.name, email: m?.email, permission: 'view' }]);
          }}
        >
          Добавить
        </button>
      </div>
    </Modal>
  );
}

function DeleteDialog({ ctl, id }: { ctl: Ctl; id: string }) {
  const r = ctl.res(id);
  const [busy, setBusy] = useState(false);
  if (!r) return null;
  const kids = (ctl.state.snap?.resources ?? []).filter((x: Res) => x.parent_id === id).length;
  const del = async () => {
    setBusy(true);
    try {
      await ctl.api.deleteResource(id);
      overlay.close();
      if (ctl.state.openId === id) ctl.setState({ openId: null, view: 'home' });
      await ctl.refresh();
      overlay.snack(`«${r.title}» в корзине`, {
        label: 'Вернуть',
        run: () =>
          ctl.api.restore(id).then(
            () => {
              overlay.set({ snack: null });
              return ctl.refresh();
            },
            (e: any) => overlay.snack(errText(e)),
          ),
      });
    } catch (e) {
      setBusy(false);
      overlay.snack(errText(e));
    }
  };
  return (
    <Modal
      title={`Удалить «${r.title}»?`}
      sub={
        (r.kind === 'folder' && kids ? `Вместе с содержимым (${kids}). ` : '') +
        'Материал попадёт в корзину проекта — его можно восстановить в течение 30 дней.'
      }
      onClose={overlay.close}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btn()} onClick={overlay.close}>
          Отмена
        </button>
        <button style={{ ...btn(false, true), opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={del} autoFocus>
          Удалить
        </button>
      </div>
    </Modal>
  );
}

function TrashDialog({ ctl }: { ctl: Ctl }) {
  const pid = ctl.state.pid;
  const [items, setItems] = useState<any[] | null>(null);
  const load = () => ctl.api.trash(pid).then(setItems, (e: any) => overlay.snack(errText(e)));
  useEffect(() => {
    load();
  }, [pid]);
  const restore = async (id: string) => {
    try {
      await ctl.api.restore(id);
      await ctl.refresh();
      load();
      overlay.snack('Восстановлено');
    } catch (e) {
      overlay.snack(errText(e));
    }
  };
  const kindOf = (x: any) =>
    x.kind === 'doc'
      ? 'file-text'
      : x.kind === 'folder'
        ? 'folder'
        : x.kind === 'board'
          ? 'sticky-note'
          : x.kind === 'mindmap'
            ? 'network'
            : 'square-check-big';
  return (
    <Modal
      title="Корзина проекта"
      sub="Удалённое хранится 30 дней, потом стирается."
      onClose={overlay.close}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 380, overflow: 'auto' }}>
        {items === null && <div style={{ color: 'hsl(var(--muted-foreground))' }}>Загружаем…</div>}
        {items?.length === 0 && (
          <div style={{ color: 'hsl(var(--muted-foreground))', padding: '8px 0' }}>Корзина пуста.</div>
        )}
        {items?.map((x) => (
          <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
            <Icon name={kindOf(x)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {x.title}
              </div>
              <div style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))' }}>
                {new Date(x.deletedAt).toLocaleString('ru-RU', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {x.itemCount > 1 ? ` · ${x.itemCount} материалов` : ''}
              </div>
            </div>
            <button style={btn()} onClick={() => restore(x.id)}>
              <Icon name="undo-2" size={14} />
              Восстановить
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ---------- white list (ADR-017): экран ожидания и заявки ----------

/** Аккаунт ждёт одобрения или отклонён: вместо приложения — этот экран (в дизайне его нет). */
function PendingScreen({ ctl }: { ctl: Ctl }) {
  const me = ctl._me;
  const rejected = me.access === 'rejected';
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try {
      const fresh = await http('/me');
      if (fresh.access === 'approved') location.reload();
      else overlay.snack(fresh.access === 'rejected' ? 'Заявка отклонена' : 'Заявка ещё на рассмотрении');
    } catch (e) {
      overlay.snack(errText(e));
    }
    setBusy(false);
  };
  const logout = () => http('/auth/logout', 'POST').finally(() => location.replace('/'));
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 390,
        background: 'hsl(var(--background))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="main"
        style={{ ...panel, borderRadius: 18, width: '100%', maxWidth: 420, padding: 28, boxShadow: 'none' }}
      >
        <img src="/logo.svg" alt="socrat.dev" style={{ height: 22, marginBottom: 24 }} />
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: 'hsl(var(--secondary))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <Icon name={rejected ? 'lock' : 'hourglass'} size={18} />
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 650, letterSpacing: '-0.015em', margin: '0 0 8px' }}>
          {rejected ? 'Доступ пока закрыт' : 'Заявка отправлена'}
        </h1>
        <p style={{ margin: '0 0 6px', lineHeight: 1.55, color: 'hsl(var(--muted-foreground))' }}>
          {rejected
            ? 'Socrat сейчас в закрытом доступе, и вашу заявку не одобрили. Мы напишем, когда откроем регистрацию.'
            : 'Socrat сейчас в закрытом доступе. Мы проверим заявку и откроем доступ — после этого вход заработает сам.'}
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 12.5, color: 'hsl(var(--muted-foreground))' }}>{me.email}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          {!rejected && (
            <button style={btn(true)} disabled={busy} onClick={check}>
              <Icon name="refresh-cw" size={14} />
              Проверить снова
            </button>
          )}
          <button style={btn()} onClick={logout}>
            Выйти
          </button>
        </div>
      </div>
    </div>
  );
}

const ACCESS_TABS = [
  ['pending', 'Ждут'],
  ['approved', 'Одобрены'],
  ['rejected', 'Отклонены'],
  ['list', 'Список почт'],
] as const;
type AccessTab = (typeof ACCESS_TABS)[number][0];

/** Заявки на доступ — только администратору через WireGuard (сервер иначе отвечает 404). */
function AdmissionDialog({ ctl }: { ctl: Ctl }) {
  const [tab, setTab] = useState<AccessTab>('pending');
  const [rows, setRows] = useState<any[] | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const api = ctl.api;
  const fail = (e: any) =>
    overlay.snack(e?.status === 404 ? 'Заявки открываются только с включённым WireGuard' : errText(e));
  const load = (t: AccessTab = tab) => {
    setRows(null);
    (t === 'list'
      ? api.adminAllowlist()
      : api.adminAccess(t).then((r: any) => {
          setPending(r.pending);
          return r.items;
        })
    ).then(setRows, fail);
  };
  useEffect(() => load(tab), [tab]);
  const decide = async (id: string, decision: 'approve' | 'reject') => {
    try {
      await api.adminDecide(id, decision);
      overlay.snack(decision === 'approve' ? 'Доступ открыт' : 'Заявка отклонена');
      load();
    } catch (e) {
      fail(e);
    }
  };
  const allow = async () => {
    const em = email.trim();
    if (!em) return;
    try {
      const r = await api.adminAllow(em);
      setEmail('');
      overlay.snack(r.approved ? 'Добавлено — ждавший аккаунт одобрен' : 'Добавлено: регистрация откроется сразу');
      load();
    } catch (e) {
      fail(e);
    }
  };
  const remove = (em: string) => api.adminDisallow(em).then(() => load(), fail);
  const date = (d: string) =>
    new Date(d).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const muted = { fontSize: 11.5, color: 'hsl(var(--muted-foreground))' } as const;
  return (
    <Modal
      title="Заявки на доступ"
      sub="Закрытый доступ: новые аккаунты ждут одобрения. Почты из списка проходят сразу."
      onClose={overlay.close}
      width={560}
    >
      <div
        role="tablist"
        style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 12, background: 'hsl(var(--muted))' }}
      >
        {ACCESS_TABS.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            style={{
              flex: 1,
              height: 30,
              border: 0,
              borderRadius: 9,
              background: tab === k ? 'hsl(var(--background))' : 'transparent',
              boxShadow: tab === k ? '0 1px 3px rgba(16,24,40,.12)' : 'none',
              color: 'hsl(var(--foreground))',
              fontWeight: 600,
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            {label}
            {k === 'pending' && pending ? ` · ${pending}` : ''}
          </button>
        ))}
      </div>
      {tab === 'list' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            allow();
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <input
            aria-label="Почта"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={input}
          />
          <button type="submit" style={btn(true)}>
            Добавить
          </button>
        </form>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 380, overflow: 'auto' }}>
        {rows === null && <div style={{ color: 'hsl(var(--muted-foreground))' }}>Загружаем…</div>}
        {rows?.length === 0 && (
          <div style={{ color: 'hsl(var(--muted-foreground))', padding: '8px 0' }}>
            {tab === 'pending' ? 'Новых заявок нет.' : tab === 'list' ? 'Список пуст.' : 'Пусто.'}
          </div>
        )}
        {rows?.map((x) =>
          tab === 'list' ? (
            <div key={x.email} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
              <Icon name="mail-check" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{x.email}</div>
                <div style={muted}>добавлена {date(x.createdAt)}</div>
              </div>
              <button aria-label={'Убрать ' + x.email} style={btn()} onClick={() => remove(x.email)}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ) : (
            <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {x.name} <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))' }}>{x.email}</span>
                </div>
                <div style={muted}>
                  {tab === 'pending' ? 'заявка ' + date(x.createdAt) : 'решение ' + date(x.decidedAt ?? x.createdAt)}
                  {x.emailVerified ? ' · почта подтверждена' : ''}
                </div>
              </div>
              {tab !== 'approved' && (
                <button style={btn(true)} onClick={() => decide(x.id, 'approve')}>
                  Одобрить
                </button>
              )}
              {tab !== 'rejected' && (
                <button style={btn()} onClick={() => decide(x.id, 'reject')}>
                  {tab === 'approved' ? 'Закрыть доступ' : 'Отклонить'}
                </button>
              )}
            </div>
          ),
        )}
      </div>
    </Modal>
  );
}

// ---------- ⌘K ----------

/** Найденное сервером: текст материалов и другие проекты и спейсы (ADR-014). */
type Hit = { id: string; kind: string; title: string; projectId: string; projectName: string; spaceName: string };
const HIT_ICON: Record<string, string> = {
  doc: 'file-text',
  mindmap: 'network',
  board: 'sticky-note',
  task: 'square-check-big',
};

/** Поиск на сервере по мере набора: пауза 150 мс, устаревшие ответы отбрасываются. */
function useServerSearch(ctl: Ctl, q: string) {
  const [hits, setHits] = useState<{ q: string; items: Hit[] }>({ q: '', items: [] });
  useEffect(() => {
    const query = q.trim();
    if (!query || !ctl.live || !ctl.api?.search) return;
    let alive = true;
    const t = setTimeout(() => {
      ctl.api.search(query).then(
        (r: { items: Hit[] }) => alive && setHits({ q: query, items: r.items }),
        () => {}, // палитра работает и без сервера — по названиям файлов проекта
      );
    }, 150);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [ctl, q]);
  return hits.q === q.trim() ? hits.items : [];
}

function Palette({ ctl }: { ctl: Ctl }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const rs: Res[] = ctl.state.snap?.resources ?? [];
  const hits = useServerSearch(ctl, q);
  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    // Сначала совпадения в названии (с начала — выше), потом по тексту.
    const score = (r: Res) => {
      if (!query) return 2;
      const t = r.title.toLowerCase();
      if (t.startsWith(query)) return 3;
      if (t.includes(query)) return 2;
      return String(r.content?.text ?? '')
        .toLowerCase()
        .includes(query)
        ? 1
        : 0;
    };
    const ranked = rs
      .filter((r) => r.kind !== 'task')
      .map((r) => [r, score(r)] as const)
      .filter(([, sc]) => sc > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    const toItem = (r: Res) => ({
      key: r.id,
      icon: kindIcon(r),
      label: r.title,
      hint: kindLabel(r),
      run: () => {
        overlay.close();
        ctl.open(r.id);
      },
    });
    const byTitle = ranked.filter(([, sc]) => sc > 1).map(([r]) => toItem(r));
    const shown = new Set(byTitle.map((i) => i.key));
    const byText = ranked.filter(([, sc]) => sc === 1).map(([r]) => toItem(r));
    byText.forEach((i) => shown.add(i.key));
    // с сервера: совпадения в тексте (морфология, опечатки) и материалы других проектов
    const pid = ctl.state.pid;
    const remote = hits
      .filter((h) => !shown.has(h.id))
      .map((h) => ({
        key: h.id,
        icon: HIT_ICON[h.kind] ?? 'file-text',
        label: h.title,
        hint: h.projectId === pid ? 'В проекте' : `${h.projectName} · ${h.spaceName}`,
        here: h.projectId === pid,
        run: () => {
          overlay.close();
          ctl.openLink(h.projectId, h.id);
        },
      }));
    const people = query
      ? ((ctl.state.memData?.[ctl.state.sid]?.list ?? []) as { userId: string; name: string; email: string }[])
          .filter((m) => m.name.toLowerCase().includes(query) || m.email?.toLowerCase().startsWith(query))
          .slice(0, 4)
          .map((m) => ({
            key: 'user:' + m.userId,
            icon: 'user',
            label: m.name,
            hint: 'Участник спейса',
            run: () => {
              overlay.close();
              ctl.setState({ mwOpen: true, mwPeople: true, inviting: false });
            },
          }))
      : [];
    const commands = [
      {
        key: 'new-note',
        icon: 'file-plus',
        label: 'Новый документ',
        hint: 'Создать',
        run: () => {
          overlay.close();
          ctl.create('note');
        },
      },
      {
        key: 'new-map',
        icon: 'network',
        label: 'Новая майнд-карта',
        hint: 'Создать',
        run: () => {
          overlay.close();
          ctl.create('mindmap');
        },
      },
      {
        key: 'new-board',
        icon: 'sticky-note',
        label: 'Новая доска',
        hint: 'Создать',
        run: () => {
          overlay.close();
          ctl.create('board');
        },
      },
      {
        key: 'new-folder',
        icon: 'folder-plus',
        label: 'Новая папка',
        hint: 'Создать',
        run: () => {
          overlay.close();
          ctl.create('folder');
        },
      },
      // В новом дизайне нет пунктов навигации «Задачи» и «Цели и KPI» — экраны остались, открываем отсюда.
      // Пока задачи в разработке (ADR-017) — команд нет.
      ...(FEAT.tasks
        ? [
            {
              key: 'tasks',
              icon: 'square-kanban',
              label: 'Задачи проекта',
              hint: 'Открыть',
              run: () => {
                overlay.close();
                ctl.go('tasks');
              },
            },
            {
              key: 'kpi',
              icon: 'target',
              label: 'Цели и KPI',
              hint: 'Открыть',
              run: () => {
                overlay.close();
                ctl.go('kpi');
              },
            },
          ]
        : []),
      ...(ctl._admin
        ? [
            {
              key: 'admission',
              icon: 'user-check',
              label: 'Заявки на доступ',
              hint: 'Открыть',
              run: () => overlay.openDialog('admission'),
            },
          ]
        : []),
      {
        key: 'trash',
        icon: 'archive-restore',
        label: 'Корзина проекта',
        hint: 'Открыть',
        run: () => overlay.openDialog('trash'),
      },
      {
        key: 'settings',
        icon: 'settings',
        label: 'Настройки аккаунта',
        hint: 'Открыть',
        run: () => {
          overlay.close();
          ctl.go('settings');
        },
      },
    ].filter((c) => !query || c.label.toLowerCase().includes(query));
    // Без запроса — файлы, потом команды; с запросом команда по названию выше совпадений в тексте файлов.
    const here = remote.filter((r) => r.here);
    const elsewhere = remote.filter((r) => !r.here);
    return [...byTitle, ...here, ...people, ...commands, ...byText, ...elsewhere];
  }, [q, rs, ctl, hits]);
  const run = (i: number) => results[i]?.run();
  return (
    <div
      onMouseDown={(e) => e.target === e.currentTarget && overlay.close()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 400,
        background: 'rgba(16,20,28,.32)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '14vh 16px 16px',
      }}
    >
      <div
        role="dialog"
        aria-label="Поиск и команды"
        style={{ ...panel, borderRadius: 18, width: '100%', maxWidth: 560, overflow: 'hidden' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 14px',
            borderBottom: '1px solid hsl(var(--border))',
          }}
        >
          <Icon name="search" style={{ opacity: 0.55 }} />
          <input
            autoFocus
            value={q}
            placeholder="Поиск по файлам проекта и командам…"
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSel((s) => Math.min(results.length - 1, s + 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSel((s) => Math.max(0, s - 1));
              }
              if (e.key === 'Enter') run(sel);
              if (e.key === 'Escape') overlay.close();
            }}
            style={{ ...input, border: 0, height: 52, fontSize: 15, padding: 0, background: 'transparent' }}
          />
          <kbd
            style={{
              fontSize: 11,
              color: 'hsl(var(--muted-foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 6,
              padding: '2px 6px',
            }}
          >
            esc
          </kbd>
        </div>
        <div style={{ maxHeight: 380, overflow: 'auto', padding: 6 }}>
          {results.map((r, i) => (
            <div
              key={r.key}
              onMouseEnter={() => setSel(i)}
              onClick={() => run(i)}
              style={{
                height: 38,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '0 10px',
                borderRadius: 10,
                cursor: 'pointer',
                background: i === sel ? 'hsl(var(--accent))' : 'transparent',
              }}
            >
              <Icon name={r.icon} style={{ opacity: 0.7 }} />
              <span
                style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {r.label}
              </span>
              <span style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))' }}>{r.hint}</span>
            </div>
          ))}
          {!results.length && (
            <div style={{ padding: 14, color: 'hsl(var(--muted-foreground))' }}>Ничего не найдено</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- кнопка «⋯» у строки дерева ----------

/** Текст элемента конфликта: «ваш» и «их» вариант (для документа — блок, для карты — узел). */
function conflictRows(c: any): { label: string; mine: string; theirs: string }[] {
  const cut = (t: string) => (t.length > 280 ? t.slice(0, 279) + '…' : t) || '—';
  const gone = '(удалено)';
  if (c.kind === 'doc') {
    const find = (bs: any[], id: string) => (bs ?? []).find((b) => b.id === id);
    return c.items.map((item: string) => {
      if (item === 'blocks')
        return {
          label: 'Весь документ',
          mine: cut(c.mine.md ?? blocksToText(c.mine.blocks)),
          theirs: cut(c.theirs.content.text ?? blocksToText(c.theirs.content.blocks)),
        };
      if (item === 'title') return { label: 'Название', mine: c.mine.title, theirs: c.theirs.title };
      if (item.startsWith('page:')) {
        const k = item.slice(5);
        return {
          label: 'Свойство «' + k + '»',
          mine: JSON.stringify(c.mine.page?.[k] ?? null),
          theirs: JSON.stringify(c.theirs.content.page?.[k] ?? null),
        };
      }
      const id = item.slice(6);
      const m = find(c.mine.blocks, id),
        t = find(c.theirs.content.blocks, id);
      return { label: 'Блок', mine: m ? cut(blocksToText([m])) : gone, theirs: t ? cut(blocksToText([t])) : gone };
    });
  }
  return c.items.map((item: string) => {
    const [k, id] = [item.slice(0, item.indexOf(':')), item.slice(item.indexOf(':') + 1)];
    if (k === 'style')
      return {
        label: 'Оформление «' + id + '»',
        mine: JSON.stringify(c.mine.style?.[id] ?? null),
        theirs: JSON.stringify(c.theirs.content.style?.[id] ?? null),
      };
    const m = (c.mine[k] ?? []).find((x: any) => x.id === id),
      t = (c.theirs.content[k] ?? []).find((x: any) => x.id === id);
    const text = (x: any) => (x ? cut(x.data?.label || x.label || (k === 'nodes' ? 'узел' : 'связь')) : gone);
    return {
      label: k === 'nodes' ? 'Узел' : k === 'edges' || k === 'rels' ? 'Связь' : 'Рамка',
      mine: text(m),
      theirs: text(t),
    };
  });
}

function ConflictDialog({ ctl, id }: { ctl: Ctl; id: string }) {
  const c = ctl.conflicts?.[id];
  const [busy, setBusy] = useState(false);
  if (!c?.theirs) return null;
  const rows = conflictRows(c);
  const title = c.theirs.title;
  const pick = (choice: 'mine' | 'theirs' | 'merge') => {
    setBusy(true);
    ctl.resolveConflict(id, choice).finally(() => setBusy(false));
  };
  const cell: CSSProperties = {
    flex: 1,
    minWidth: 0,
    padding: '8px 10px',
    borderRadius: 10,
    background: 'hsl(var(--muted) / .6)',
    fontSize: 12.5,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    maxHeight: 120,
    overflow: 'auto',
  };
  return (
    <Modal
      title="Файл изменил другой участник"
      sub={`Пока вы работали, «${title}» сохранили в другом месте, и часть правок пересекается. Выберите, что оставить — остальные правки обеих сторон сохранятся при объединении.`}
      onClose={() => {}}
      width={620}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          fontSize: 11,
          fontWeight: 600,
          color: 'hsl(var(--muted-foreground))',
          padding: '0 2px 4px',
        }}
      >
        <span style={{ flex: 1 }}>Ваш вариант</span>
        <span style={{ flex: 1 }}>Их вариант</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          maxHeight: '44vh',
          overflow: 'auto',
          marginBottom: 14,
        }}
      >
        {rows.slice(0, 20).map((r, i) => (
          <div key={i}>
            <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>{r.label}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={cell}>{r.mine}</div>
              <div style={cell}>{r.theirs}</div>
            </div>
          </div>
        ))}
        {rows.length > 20 && (
          <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>И ещё {rows.length - 20}…</div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', opacity: busy ? 0.7 : 1 }}>
        <button
          style={btn()}
          disabled={busy}
          onClick={() => pick('theirs')}
          title="Ваши правки этого файла будут отменены"
        >
          Взять их версию
        </button>
        {!c.items.includes('blocks') && (
          <button
            style={btn()}
            disabled={busy}
            onClick={() => pick('merge')}
            title="Где правки пересекаются — их вариант, остальное объединится"
          >
            Объединить, в спорном — их
          </button>
        )}
        <button
          style={btn(true)}
          disabled={busy}
          onClick={() => pick('mine')}
          title="Их правки этого файла будут перезаписаны"
        >
          Оставить мою версию
        </button>
      </div>
    </Modal>
  );
}

function HoverDots({ ctl }: { ctl: Ctl }) {
  const [hover, setHover] = useState<{ id: string; rect: DOMRect } | null>(null);
  useEffect(() => {
    const onOver = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest?.('[data-dots]')) return;
      const row = t.closest?.('aside [data-dc-key]') as HTMLElement | null;
      setHover(row ? { id: row.dataset.dcKey!, rect: row.getBoundingClientRect() } : null);
    };
    document.addEventListener('mouseover', onOver);
    return () => document.removeEventListener('mouseover', onOver);
  }, []);
  if (!hover || !ctl.res(hover.id)) return null;
  const { rect } = hover;
  return (
    <button
      data-dots="1"
      aria-label="Действия с файлом"
      onMouseLeave={(e) => {
        if (!(e.relatedTarget as HTMLElement)?.closest?.('[data-dc-key]')) setHover(null);
      }}
      onClick={(e) => {
        const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
        overlay.openMenu(hover.id, b.left, b.bottom + 4);
      }}
      style={{
        position: 'fixed',
        zIndex: 60,
        left: rect.right - 30,
        top: rect.top + (rect.height - 24) / 2,
        width: 24,
        height: 24,
        border: 0,
        borderRadius: 7,
        cursor: 'pointer',
        background: 'hsl(var(--background))',
        color: 'hsl(var(--muted-foreground))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(16,24,40,.12)',
      }}
    >
      <Icon name="ellipsis" size={14} />
    </button>
  );
}

// ---------- корень ----------

export function Overlays({ ctl }: { ctl: Ctl }) {
  const st = useOverlay();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && ctl.state.snap && !ctl.state.au) {
        e.preventDefault();
        if (overlay.get().dialog?.kind === 'palette') overlay.close();
        else overlay.openDialog('palette');
      }
      // F2 — открытый файл или выбранная задача на канбане (в панели задачи дизайна название не редактируется)
      const target = ctl.state.openId || (ctl.state.view === 'tasks' ? ctl.state.selId : null);
      if (e.key === 'F2' && target && overlay.get().dialog == null) {
        const r = ctl.res(target);
        if (can(r, 'edit')) overlay.openDialog('rename', r.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctl]);
  if (!ctl) return null;
  const d = st.dialog;
  return (
    <div data-theme={ctl.state.ui?.theme || 'light'} style={{ fontFamily: 'inherit' }}>
      {ctl.state.snap && !ctl.state.au && <HoverDots ctl={ctl} />}
      {st.menu && <FileMenu ctl={ctl} {...st.menu} />}
      {d?.kind === 'rename' && d.id && <RenameDialog key={d.id} ctl={ctl} id={d.id} />}
      {d?.kind === 'move' && d.id && <MoveDialog key={d.id} ctl={ctl} id={d.id} />}
      {d?.kind === 'access' && d.id && <AccessDialog key={d.id} ctl={ctl} id={d.id} />}
      {d?.kind === 'delete' && d.id && <DeleteDialog key={d.id} ctl={ctl} id={d.id} />}
      {d?.kind === 'trash' && <TrashDialog ctl={ctl} />}
      {d?.kind === 'palette' && <Palette ctl={ctl} />}
      {d?.kind === 'conflict' && d.id && <ConflictDialog key={d.id} ctl={ctl} id={d.id} />}
      {d?.kind === 'admission' && ctl._admin && <AdmissionDialog ctl={ctl} />}
      {ctl._me && ctl._me.access && ctl._me.access !== 'approved' && <PendingScreen ctl={ctl} />}
      {st.snack && (
        <div
          role="status"
          style={{
            ...panel,
            position: 'fixed',
            zIndex: 420,
            right: 20,
            bottom: 20,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            maxWidth: 420,
          }}
        >
          <span>{st.snack.text}</span>
          {st.snack.action && (
            <button
              onClick={st.snack.action.run}
              style={{
                border: 0,
                background: 'transparent',
                color: 'hsl(var(--primary))',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {st.snack.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
